/**
 * Activity Logging Client
 * 
 * Utility for logging user activities from the web app.
 * Sends activity logs to the API asynchronously and fails silently.
 */

import { getApiUrl } from './api';
import { useAuthStore } from '@/stores/auth-store';

export type ActivityAction =
  | 'VIEW_PAGE'
  | 'VIEW_CHECKLIST'
  | 'TOGGLE_ITEM'
  | 'CREATE_ITEM'
  | 'UPDATE_ITEM'
  | 'DELETE_ITEM'
  | 'CREATE_CUSTOM_CHECKLIST'
  | 'UPDATE_CUSTOM_CHECKLIST'
  | 'DELETE_CUSTOM_CHECKLIST'
  | 'RESET_RECURRING_CHECKLIST'
  | 'VIEW_INVENTORY'
  | 'ADD_INVENTORY_ITEM'
  | 'UPDATE_INVENTORY_ITEM'
  | 'DELETE_INVENTORY_ITEM'
  | 'CREATE_FAMILY'
  | 'INVITE_FAMILY_MEMBER'
  | 'REMOVE_FAMILY_MEMBER'
  | 'LEAVE_FAMILY'
  | 'JOIN_FAMILY'
  | 'UPDATE_FAMILY'
  | 'VIEW_FAMILY'
  | 'SEND_MESSAGE'
  | 'DELETE_FAMILY_MESSAGE'
  | 'VIEW_GROUP'
  | 'JOIN_GROUP'
  | 'LEAVE_GROUP'
  | 'CREATE_GROUP'
  | 'CREATE_THREAD'
  | 'REPLY_TO_THREAD'
  | 'CREATE_BULLETIN_POST'
  | 'UPDATE_BULLETIN_POST'
  | 'DELETE_BULLETIN_POST'
  | 'CREATE_NOTE'
  | 'UPDATE_NOTE'
  | 'DELETE_NOTE'
  | 'ADD_WATER_CONTAINER'
  | 'UPDATE_WATER_CONTAINER'
  | 'DELETE_WATER_CONTAINER'
  | 'ROTATE_WATER'
  | 'ADD_SHOPPING_ITEM'
  | 'UPDATE_SHOPPING_ITEM'
  | 'DELETE_SHOPPING_ITEM'
  | 'ADD_FAVORITE'
  | 'REMOVE_FAVORITE'
  | 'VIEW_WIKI'
  | 'EDIT_WIKI'
  | 'READ_ARTICLE'
  | 'VIEW_CALENDAR'
  | 'ADD_EVENT'
  | 'COMPLETE_YEAR_WHEEL_TASK'
  | 'CREATE_YEAR_WHEEL_TASK'
  | 'UPDATE_YEAR_WHEEL_TASK'
  | 'DELETE_YEAR_WHEEL_TASK'
  | 'CREATE_RECIPE'
  | 'UPDATE_RECIPE'
  | 'DELETE_RECIPE'
  | 'COMPLETE_PREP_TEST'
  | 'UPDATE_PREP_RECOMMENDATION'
  | 'UPDATE_SETTINGS'
  | 'LOGIN'
  | 'LOGOUT';

export interface LogActivityOptions {
  action: ActivityAction;
  category?: string;
  description?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Log a user activity.
 * Fails silently if logging fails to avoid disrupting user experience.
 */
export async function logActivity(options: LogActivityOptions): Promise<void> {
  try {
    const { accessToken } = useAuthStore.getState();
    if (!accessToken) {
      // User not authenticated, skip logging
      console.log('[Activity Logger] No token found, skipping');
      return;
    }

    console.log('[Activity Logger] Logging activity:', options.action);
    const response = await fetch(getApiUrl('activity/log'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        action: options.action,
        category: options.category,
        description: options.description,
        metadata: options.metadata,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      console.warn('[Activity Logger] Failed to log activity:', response.status, text);
    } else {
      console.log('[Activity Logger] Successfully logged:', options.action);
    }
  } catch (error) {
    // Fail silently - don't disrupt user experience
    console.warn('[Activity Logger] Error:', error);
  }
}

/**
 * Log a page view.
 * Convenience wrapper for VIEW_PAGE action.
 */
export function logPageView(pageName: string, metadata?: Record<string, unknown>): void {
  logActivity({
    action: 'VIEW_PAGE',
    category: 'page',
    description: `Viewing ${pageName}`,
    metadata,
  });
}

/**
 * Log a checklist interaction.
 */
export function logChecklistAction(
  action: 'VIEW_CHECKLIST' | 'TOGGLE_ITEM' | 'CREATE_ITEM' | 'UPDATE_ITEM' | 'DELETE_ITEM',
  checklistId: string,
  metadata?: Record<string, unknown>
): void {
  logActivity({
    action,
    category: 'checklist',
    description: `${action} - ${checklistId}`,
    metadata,
  });
}

/**
 * Log an inventory interaction.
 */
export function logInventoryAction(
  action: 'VIEW_INVENTORY' | 'ADD_INVENTORY_ITEM' | 'UPDATE_INVENTORY_ITEM' | 'DELETE_INVENTORY_ITEM',
  itemId?: string,
  metadata?: Record<string, unknown>
): void {
  logActivity({
    action,
    category: 'inventory',
    description: itemId ? `${action} - ${itemId}` : action,
    metadata,
  });
}

/**
 * Log a family interaction.
 */
export function logFamilyAction(
  action: 'VIEW_FAMILY' | 'SEND_MESSAGE' | 'DELETE_FAMILY_MESSAGE' | 'CREATE_FAMILY' | 'INVITE_FAMILY_MEMBER' | 'REMOVE_FAMILY_MEMBER' | 'LEAVE_FAMILY' | 'JOIN_FAMILY' | 'UPDATE_FAMILY',
  familyId?: string,
  metadata?: Record<string, unknown>
): void {
  logActivity({
    action,
    category: 'family',
    description: familyId ? `${action} - ${familyId}` : action,
    metadata,
  });
}

/**
 * Log a group interaction.
 */
export function logGroupAction(
  action: 'VIEW_GROUP' | 'JOIN_GROUP' | 'LEAVE_GROUP' | 'CREATE_GROUP' | 'CREATE_THREAD' | 'REPLY_TO_THREAD' | 'CREATE_BULLETIN_POST' | 'UPDATE_BULLETIN_POST' | 'DELETE_BULLETIN_POST',
  groupId?: string,
  metadata?: Record<string, unknown>
): void {
  logActivity({
    action,
    category: 'group',
    description: groupId ? `${action} - ${groupId}` : action,
    metadata,
  });
}

/**
 * Log a custom checklist action.
 */
export function logCustomChecklistAction(
  action: 'CREATE_CUSTOM_CHECKLIST' | 'UPDATE_CUSTOM_CHECKLIST' | 'DELETE_CUSTOM_CHECKLIST' | 'RESET_RECURRING_CHECKLIST',
  listId?: string,
  metadata?: Record<string, unknown>
): void {
  logActivity({
    action,
    category: 'checklist',
    description: listId ? `${action} - ${listId}` : action,
    metadata,
  });
}

/**
 * Log a year wheel action.
 */
export function logYearWheelAction(
  action: 'COMPLETE_YEAR_WHEEL_TASK' | 'CREATE_YEAR_WHEEL_TASK' | 'UPDATE_YEAR_WHEEL_TASK' | 'DELETE_YEAR_WHEEL_TASK',
  taskId?: string,
  metadata?: Record<string, unknown>
): void {
  logActivity({
    action,
    category: 'yearwheel',
    description: taskId ? `${action} - ${taskId}` : action,
    metadata,
  });
}

/**
 * Log a knowledge/wiki action.
 */
export function logKnowledgeAction(
  action: 'VIEW_WIKI' | 'EDIT_WIKI' | 'READ_ARTICLE',
  articleId?: string,
  metadata?: Record<string, unknown>
): void {
  logActivity({
    action,
    category: 'knowledge',
    description: articleId ? `${action} - ${articleId}` : action,
    metadata,
  });
}

/**
 * Log a prep test action.
 */
export function logPrepTestAction(
  action: 'COMPLETE_PREP_TEST' | 'UPDATE_PREP_RECOMMENDATION',
  metadata?: Record<string, unknown>
): void {
  logActivity({
    action,
    category: 'prep',
    description: action,
    metadata,
  });
}
